/**
 * Recognises the browser's OPAQUE cross-origin script error — the bare
 * `Script error.` with no file, no line and no stack — so PostHog Error
 * Tracking can drop it instead of filing it as a Panda shell crash.
 *
 *   Error: Script error.
 *     (no filename, lineno 0, colno 0, no error object)
 *
 * (PostHog issue 01a02c46-2d36-7062-9a5d-7163f2fa2a62 — 4 users / 4 sessions on
 * mobile homepage and `/project/:id` traffic, 2026-08-22 → 2026-08-25.)
 *
 * WHAT IT ACTUALLY IS. Per the HTML spec, when a script loaded from ANOTHER
 * origin without CORS approval throws, the browser "mutes" the error before
 * handing it to `window.onerror`: the message is replaced with the literal
 * `"Script error."`, the filename with `""`, and line/column with `0`. The
 * failing script's real message and stack are withheld from the page on
 * purpose — a page must not be able to read the internals of a third-party
 * script it cannot see the source of. So this string is not an error Panda can
 * fix; it is the browser telling us it will not say what the error was.
 *
 * WHY IT IS NEVER OURS. The muting rule only applies to scripts from another
 * origin. Panda's own code is served same-origin from `/_next/static/…` on
 * `panda.new`, and its errors therefore always arrive with a real message and a
 * real stack. Nothing in this repo throws the literal string `Script error.`.
 * So whatever threw, it was not Panda code — which is the whole reason this is
 * safe to drop rather than investigate.
 *
 * WHAT IT COULD HAVE BEEN. Deliberately NOT narrowed to the root layout's own
 * tags, because several other sources produce the identical husk and we cannot
 * tell them apart — that is the point of the muting:
 *
 *   - The third-party tags in `app/layout.tsx` (GTM, GA, the Meta pixel, the
 *     Affonso pixel) and the LeadConnector chat widget. These are the ones the
 *     `crossOrigin` fix addresses.
 *   - Tags GTM itself injects at runtime. GTM creates those script elements
 *     internally and sets no `crossorigin`, so they stay muted no matter what
 *     we do to the GTM loader.
 *   - Scripts injected by mobile in-app browsers. Highly relevant here: the
 *     sampled sessions are Chrome Android and Mobile Safari on paid Meta
 *     traffic, i.e. Facebook/Instagram in-app browser shells, which inject
 *     their own instrumentation (see in-app-browser-noise.ts for the bridge
 *     errors from the same shells that DO carry an identifiable source).
 *   - Browser extension content scripts.
 *   - Vendor SDKs that inject their own tag, notably Stripe.js and Paddle.js
 *     (lib/stripe-client.ts, lib/paddle-client.ts). Both CDNs would permit CORS,
 *     but the attribute is set by the vendor SDK, not by us.
 *
 * SO DO NOT EXPECT THE COUNT TO REACH EXACTLY ZERO FROM THE `crossOrigin` HALF
 * ALONE. It removes the muting for the tags we own; the rest of that list stays
 * muted and is what this predicate exists to absorb.
 *
 * WHY IT ONLY EVER SHOWED UP IN POSTHOG. Sentry's `eventFilters` integration
 * ships `/^Script error\.?$/` in its DEFAULT_IGNORE_ERRORS list and drops these
 * as an event processor — before `beforeSend` ever runs — so the Sentry issue
 * stream has silently never contained them. posthog-js `capture_exceptions` has
 * no equivalent default: its `window.onerror` wrapper coerces the muted MESSAGE
 * STRING (`args[4]` is null for a muted error, so it falls back to `args[0]`)
 * into `{ type: "Error", value: "Script error." }` and captures it. This
 * predicate is what brings the PostHog path to parity with the Sentry one.
 *
 * MATCH ON THE MESSAGE, NOT ON THE STACK. The sibling classifiers
 * (`cross-origin-frame-noise.ts`) refuse to drop an event when a Panda-owned
 * frame appears in the stack. That test would be actively WRONG here and would
 * stop this predicate ever firing: because the muted error carries no stack of
 * its own, posthog-js substitutes a synthetic one captured inside its own SDK —
 * and posthog-js is an npm dependency compiled into our bundle, so every frame
 * on it is `/_next/static/…` and reads as Panda-owned. The anchored message is
 * the only honest signal, which is exactly why Sentry keys on it too.
 *
 * The regex is anchored end-to-end so it matches ONLY the browser's sentinel and
 * the `Javascript error: Script error. on line 0` wording some mobile WebViews
 * use. A real error that merely mentions a script error in a longer sentence is
 * never silenced.
 *
 * ATTRIBUTION IS FIXED SEPARATELY, AND FIRST. Dropping an opaque error is only
 * defensible because the errors are no longer forced to BE opaque: every
 * third-party tag whose CDN serves CORS headers now loads with
 * `crossOrigin="anonymous"` (app/layout.tsx, components/chat-widget.tsx), which
 * switches off the muting and lets the real message, file and stack through to
 * both tools with the usual release/route/session envelope. What still reaches
 * this predicate is the residue that cannot be attributed at all — chiefly
 * `connect.facebook.net/en_US/fbevents.js`, which serves NO
 * `Access-Control-Allow-Origin` and so cannot be un-muted from our side.
 *
 * Pure and dependency-free (no Sentry or posthog-js import) so it is trivially
 * unit-testable and reusable on any capture path. It accepts the minimal event
 * shape it inspects; a real `@sentry/nextjs` `ErrorEvent` satisfies it
 * structurally, as does the reshaped PostHog `$exception`. Mirrors
 * `embedded-crawler-noise.ts` / `in-app-browser-noise.ts`.
 */

export const OPAQUE_CROSS_ORIGIN_SCRIPT_ORIGIN = "opaque-cross-origin-script" as const;

interface OpaqueScriptExceptionValue {
  /**
   * Accepted so the interface describes the events that actually arrive, but
   * deliberately not read: the same muted error is coerced to `Error` by
   * posthog-js and to `ErrorEvent` by other capture paths, and neither wording
   * adds information the message does not already carry.
   */
  type?: string;
  value?: string;
}

export interface OpaqueCrossOriginScriptNoiseEvent {
  /** Sentry's `Event.message` (string in practice; typed loose for safety). */
  message?: unknown;
  exception?: { values?: OpaqueScriptExceptionValue[] };
}

// The browser's muted-error sentinel, and nothing else. Anchored at BOTH ends so
// only the bare sentinel matches — a genuine error whose text happens to contain
// the words is never silenced. The optional `Javascript error: … on line 0`
// wrapper is the wording some mobile WebViews report the same muted error with
// (Sentry carries the identical pair in its default list). The trailing period
// is optional because Firefox omits it.
const OPAQUE_SCRIPT_ERROR_RE =
  /^(?:Javascript error:\s*)?Script error\.?(?:\s+on line 0)?$/i;

function matchesOpaqueScriptError(text: string): boolean {
  return OPAQUE_SCRIPT_ERROR_RE.test(text.trim());
}

/**
 * True when the event is the browser's opaque cross-origin `Script error.` — an
 * error the browser has deliberately stripped of message, source and stack —
 * and so should be dropped from error reporting.
 *
 * Matched against each exception value's message and, for events carrying only a
 * top-level message, against that. The exception TYPE is deliberately not part
 * of the test: the same muted error is coerced to `Error` by posthog-js and to
 * `ErrorEvent` by other capture paths, and neither adds information.
 */
export function isOpaqueCrossOriginScriptNoise(
  event: OpaqueCrossOriginScriptNoiseEvent | null | undefined,
): boolean {
  if (!event) return false;

  for (const value of event.exception?.values ?? []) {
    if (typeof value.value === "string" && matchesOpaqueScriptError(value.value)) {
      return true;
    }
  }

  // Fallback for events carrying only a top-level message (no exception values).
  const message = typeof event.message === "string" ? event.message : "";
  return matchesOpaqueScriptError(message);
}
